import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { usePromoEligibility } from '@/features/wallet/hooks/useWallet';
import { ConsultantCard } from '@/features/professionals/components/ConsultantCard';
import {
  FilterSidebar,
  FilterMobileToggle,
  type FilterValues,
} from '@/features/professionals/components/FilterSidebar';
import {
  usePublicProfessionals,
  useSpecializations,
} from '@/features/professionals/hooks/usePublicProfessionals';
import type { PublicProfessionalQueryParams } from '@/features/professionals/services/professional-public.service';

/**
 * Full consultant directory page. Reachable from the home "Vedi tutti i
 * consulenti" button and the navbar search / specialization links.
 */
export function ConsultantsPage() {
  const { isAuthenticated } = useAuth();
  const { isPromoEligible } = usePromoEligibility();
  const showPromoPrice = !isAuthenticated || isPromoEligible;

  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  // Aprendo la pagina consulenti (es. dalla home) parte sempre dall'inizio.
  const didMountRef = useRef(false);
  const listTopRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const specialization = searchParams.get('specialization') ?? undefined;
  const search = searchParams.get('search') ?? undefined;
  const minRate = searchParams.get('minRate') ? parseFloat(searchParams.get('minRate')!) : undefined;
  const maxRate = searchParams.get('maxRate') ? parseFloat(searchParams.get('maxRate')!) : undefined;
  const available = searchParams.get('available') === 'true' || undefined;
  const sortByRaw = searchParams.get('sortBy') ?? undefined;
  const page = searchParams.get('page') ? parseInt(searchParams.get('page')!, 10) : 1;

  // On pagination change, bring the list back to the top so the user starts
  // from the first consultant of the new page instead of staying scrolled low.
  // Skip the initial mount (handled by the scroll-to-(0,0) effect above).
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [page]);

  // Page-size selector: 20/50/100/200 + "Tutti" (sentinel 'all'). Default 20.
  const PAGE_SIZE_OPTIONS = [20, 50, 100, 200, 'all'] as const;
  const DEFAULT_PAGE_SIZE = 20;
  const limitParam = searchParams.get('limit');
  const limit: number | 'all' =
    limitParam === 'all'
      ? 'all'
      : (PAGE_SIZE_OPTIONS as readonly (number | 'all')[]).includes(Number(limitParam))
        ? Number(limitParam)
        : DEFAULT_PAGE_SIZE;

  const [searchInput, setSearchInput] = useState(search ?? '');

  let sortBy: PublicProfessionalQueryParams['sortBy'];
  let sortOrder: 'asc' | 'desc' | undefined;
  if (sortByRaw) {
    const [s, o] = sortByRaw.split('-') as [string, string];
    sortBy = s as PublicProfessionalQueryParams['sortBy'];
    sortOrder = o as 'asc' | 'desc';
  }

  const queryParams: PublicProfessionalQueryParams = {
    page,
    limit,
    specialization,
    search,
    minRate,
    maxRate,
    available,
    sortBy,
    sortOrder,
  };

  const { data: professionalsData, isLoading: professionalsLoading } =
    usePublicProfessionals(queryParams);
  const { data: specializations } = useSpecializations();
  const professionals = professionalsData?.data ?? [];
  const pagination = professionalsData?.pagination;

  const updateSearchParams = (updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(updates)) {
      if (value == null || value === '') {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    }
    if (!('page' in updates)) {
      next.delete('page');
    }
    setSearchParams(next, { replace: true });
  };

  const filterValues: FilterValues = { specialization, minRate, maxRate, available };

  const handleFilterChange = (filters: FilterValues) => {
    updateSearchParams({
      specialization: filters.specialization,
      minRate: filters.minRate != null ? String(filters.minRate) : undefined,
      maxRate: filters.maxRate != null ? String(filters.maxRate) : undefined,
      available: filters.available ? 'true' : undefined,
    });
  };

  const handleReset = () => {
    setSearchInput('');
    setSearchParams({}, { replace: true });
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateSearchParams({ search: searchInput.trim() || undefined });
  };

  // Active-filter chips
  const chips: { key: string; label: string; clear: () => void }[] = [];
  if (specialization) chips.push({ key: 'spec', label: specialization, clear: () => updateSearchParams({ specialization: undefined }) });
  if (search) chips.push({ key: 'search', label: `“${search}”`, clear: () => { setSearchInput(''); updateSearchParams({ search: undefined }); } });
  if (minRate != null || maxRate != null) chips.push({ key: 'price', label: `Prezzo ${minRate ?? 0}–${maxRate ?? '∞'}€`, clear: () => updateSearchParams({ minRate: undefined, maxRate: undefined }) });
  if (available) chips.push({ key: 'avail', label: 'Disponibili ora', clear: () => updateSearchParams({ available: undefined }) });

  return (
    <div className="text-brand-navy">
      {/* Breadcrumb */}
      <div className="px-4 pt-5">
        <nav className="mx-auto max-w-6xl text-xs text-neutral-400" aria-label="breadcrumb">
          <Link to="/" className="hover:text-brand-teal">Home</Link>
          <span className="mx-1.5">/</span>
          <span className="font-medium text-neutral-600">Consulenti</span>
        </nav>
      </div>

      {/* Header banner */}
      <section className="px-4 pt-3">
        <div className="mx-auto max-w-6xl overflow-hidden rounded-2xl bg-gradient-to-r from-brand-navy via-[#1c1a4d] to-brand-teal-600 px-6 py-8 sm:px-8 sm:py-10">
          <h1 className="text-2xl font-bold text-white sm:text-3xl">Tutti i Consulenti</h1>
          <p className="mt-1 text-sm text-white/70">
            Trova il consulente giusto per te tra i nostri professionisti verificati
            {pagination ? ` — ${pagination.total} disponibili` : ''}
          </p>

          {/* On-page search */}
          <form onSubmit={handleSearchSubmit} className="mt-5 flex max-w-lg gap-2">
            <div className="relative flex-1">
              <svg className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Cerca per nome o parola chiave..."
                className="h-11 w-full rounded-lg border border-white/20 bg-white/10 pl-9 pr-3 text-sm text-white placeholder-white/50 outline-none transition-colors focus:border-white/50 focus:bg-white/[0.15]"
              />
            </div>
            <button
              type="submit"
              className="rounded-lg bg-brand-orange px-5 text-sm font-bold text-white transition-colors hover:bg-brand-orange/90 min-h-[44px]"
            >
              Cerca
            </button>
          </form>
        </div>
      </section>

      {/* Directory */}
      <section ref={listTopRef} className="px-4 pb-16 pt-6">
        <div className="mx-auto max-w-6xl lg:flex lg:gap-6">
          {/* Main content */}
          <div className="min-w-0 flex-1">
            {/* Toolbar: mobile filter toggle + sort */}
            <div className="mb-4 flex items-center justify-between gap-3">
              <FilterMobileToggle filters={filterValues} onClick={() => setMobileFilterOpen(true)} />
              <div className="flex items-center gap-2">
                <select
                  value={String(limit)}
                  onChange={(e) => updateSearchParams({ limit: e.target.value === String(DEFAULT_PAGE_SIZE) ? undefined : e.target.value })}
                  aria-label="Consulenti per pagina"
                  className="h-11 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-brand-navy focus:border-brand-teal/50 focus:outline-none"
                >
                  {PAGE_SIZE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt === 'all' ? 'Tutti' : `${opt} per pagina`}
                    </option>
                  ))}
                </select>
                <select
                  value={sortByRaw ?? ''}
                  onChange={(e) => updateSearchParams({ sortBy: e.target.value || undefined })}
                  className="h-11 rounded-lg border border-neutral-200 bg-white px-3 text-sm text-brand-navy focus:border-brand-teal/50 focus:outline-none"
                >
                  <option value="">Ordina per...</option>
                  <option value="rating-desc">Rating</option>
                  <option value="price-asc">Prezzo crescente</option>
                  <option value="price-desc">Prezzo decrescente</option>
                  <option value="reviews-desc">Popolarita</option>
                </select>
              </div>
            </div>

            {/* Active-filter chips */}
            {chips.length > 0 && (
              <div className="mb-5 flex flex-wrap items-center gap-2">
                {chips.map((chip) => (
                  <button
                    key={chip.key}
                    onClick={chip.clear}
                    className="inline-flex items-center gap-1.5 rounded-full border border-brand-teal/30 bg-brand-teal/10 px-3 py-1 text-xs font-medium text-brand-teal-400 transition-colors hover:bg-brand-teal/20"
                  >
                    {chip.label}
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                ))}
                <button
                  onClick={handleReset}
                  className="text-xs font-medium text-neutral-500 underline underline-offset-2 hover:text-brand-navy"
                >
                  Cancella tutti
                </button>
              </div>
            )}

            {/* List */}
            {professionalsLoading ? (
              <div className="flex flex-col gap-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="h-32 animate-pulse rounded-xl border border-neutral-200 bg-white/60" />
                ))}
              </div>
            ) : professionals.length > 0 ? (
              <div className="flex flex-col gap-3">
                {professionals.map((pro) => (
                  <ConsultantCard key={pro.id} professional={pro} showPromoPrice={showPromoPrice} />
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-neutral-200 bg-white/60 py-16 text-center">
                <p className="text-neutral-400">Nessun consulente trovato</p>
                {chips.length > 0 && (
                  <button
                    onClick={handleReset}
                    className="mt-3 text-sm text-brand-teal underline underline-offset-2 hover:text-brand-teal-400"
                  >
                    Cancella tutti i filtri
                  </button>
                )}
              </div>
            )}

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button
                  disabled={!pagination.hasPrevPage}
                  onClick={() => updateSearchParams({ page: String(page - 1) })}
                  className="rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-600 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-30 min-h-[44px]"
                >
                  Precedente
                </button>
                <span className="px-4 text-sm text-neutral-400">
                  {pagination.page} / {pagination.totalPages}
                </span>
                <button
                  disabled={!pagination.hasNextPage}
                  onClick={() => updateSearchParams({ page: String(page + 1) })}
                  className="rounded-lg border border-neutral-200 bg-white px-4 py-2.5 text-sm text-neutral-600 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-30 min-h-[44px]"
                >
                  Successiva
                </button>
              </div>
            )}

            <div className="mt-8">
              <Link to="/" className="text-sm text-brand-teal underline underline-offset-2 hover:text-brand-teal-400">
                ← Torna alla home
              </Link>
            </div>
          </div>

          {/* Sidebar — specializations + price + availability */}
          <FilterSidebar
            filters={filterValues}
            onFilterChange={handleFilterChange}
            onReset={handleReset}
            mobileOpen={mobileFilterOpen}
            onMobileOpenChange={setMobileFilterOpen}
            specializations={specializations ?? []}
          />
        </div>
      </section>
    </div>
  );
}
